import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { formatDate, formatMoney } from "../lib/format";
import { isAccountAdmin, isManagerOrAbove } from "../lib/people";
import { usePersona } from "../lib/persona";
import { lineAmount, structureTotals, validateDeductions } from "../lib/salary";
import type { SalaryComponent, SalaryStructure, SalaryStructureLine } from "../api/types";

type ComponentKind = "earning" | "deduction";
type LineMode = "amount" | "percent_of_gross";

function money(amount: number, currency?: string | null) {
  return { amount: amount.toFixed(2), currency: currency ?? "INR" };
}


/**
 * What pay is made of. Public here -- a whole-team view an ordinary staff
 * member should never see is a data model you are one checkbox away from
 * leaking -- so the screen is honest that this is manager-and-above data.
 *
 * The one exception is the employee's own structure: reading your own pay is
 * the self-service half of the same rule (BR-32), and it is served by its own
 * endpoint that can only ever answer about the caller.
 */
export function SalaryPage() {
  const { user } = useAuth();
  const persona = usePersona();
  const timezone = user?.account?.timezone;
  const currency = user?.account?.currency;

  if (persona === "employee") return <SalarySelfView timezone={timezone} currency={currency} />;
  if (!isManagerOrAbove(user?.role)) return <SalaryBlocked />;
  return (
    <SalaryManagerView
      timezone={timezone}
      currency={currency}
      accountId={user?.account?.id}
      canEdit={isAccountAdmin(user?.role)}
    />
  );
}

function SalaryBlocked() {
  return (
    <>
      <PageHeader title="Salary" subtitle="Components and structures that make up the payroll." />
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <EmptyState
          title="Managers only"
          body="Someone in your team who manages pay can show you this. Your own payslips live on your desk."
        />
      </div>
    </>
  );
}

/** Read-only: the caller's own current structure, and nothing about anyone else. */
function SalarySelfView({
  timezone,
  currency,
}: {
  timezone?: string | null;
  currency?: string | null;
}) {
  const structure = useQuery({
    queryKey: ["my-salary-structure"],
    queryFn: ({ signal }) => api.mySalaryStructure(signal),
  });

  return (
    <>
      <PageHeader
        title="Salary"
        subtitle="Your current pay structure."
        actions={
          <Link to="/people/payslips" className="text-sm text-slate-500 hover:text-slate-700">
            My payslips &gt;
          </Link>
        }
      />
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        {structure.isPending ? (
          <TableSkeleton rows={3} />
        ) : structure.error instanceof ApiError && structure.error.status === 403 ? (
          <EmptyState
            title="Not available"
            body="Your employer has not enabled pay visibility for your role. Your payslips still live on My payslips."
          />
        ) : !structure.data ? (
          <EmptyState
            title="No salary structure yet"
            body="Your pay structure is set by the business. Payslips appear here once a payroll run is approved."
          />
        ) : (
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {structure.data.name || "Current structure"}
                </p>
                <p className="text-xs text-slate-500">
                  Effective {formatDate(structure.data.effective_from, timezone ?? undefined)}
                </p>
              </div>
              <p className="text-lg font-semibold tabular-nums text-slate-900">
                {formatMoney(money(structureTotals(structure.data).gross, currency))}
                <span className="ml-1 text-xs font-normal text-slate-500">gross monthly</span>
              </p>
            </div>
            <div className="mt-3">
              <StructureBreakdown structure={structure.data} currency={currency} />
            </div>
            <p className="mt-3 text-xs text-slate-500">
              This is your structure only. What was actually paid, and when, is on My payslips.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function SalaryManagerView({
  timezone,
  currency,
  accountId,
  canEdit,
}: {
  timezone?: string | null;
  currency?: string | null;
  accountId?: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();

  const account = useQuery({
    queryKey: ["account", accountId],
    queryFn: ({ signal }) => api.account(accountId ?? "", signal),
    enabled: Boolean(accountId),
  });

  const saveStatutory = useMutation({
    mutationFn: (body: StatutoryWrite) =>
      api.updateAccount(accountId ?? "", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    },
  });

  const components = useQuery({
    queryKey: ["salary-components", 100],
    queryFn: ({ signal }) => api.salaryComponents(signal),
  });

  const structures = useQuery({
    queryKey: ["salary-structures", 100],
    queryFn: ({ signal }) => api.salaryStructures({ page_size: 100 }, signal),
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
  });

  const createComponent = useMutation({
    mutationFn: (body: {
      name: string;
      code: string;
      kind?: ComponentKind;
      is_taxable?: boolean;
      is_active?: boolean;
    }) => api.createSalaryComponent(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["salary-components"] }),
  });

  const updateComponent = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<SalaryComponent> }) =>
      api.updateSalaryComponent(id, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["salary-components"] }),
  });

  const deleteComponent = useMutation({
    mutationFn: (id: string) => api.deleteSalaryComponent(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["salary-components"] }),
  });

  const createStructure = useMutation({
    mutationFn: (body: {
      employee: string;
      name?: string;
      effective_from: string;
      gross_monthly: string;
      lines: Array<{ component: string; calculation_mode?: LineMode; amount?: string | null; percent_of_gross?: string | null }>;
    }) =>
      api.createSalaryStructure({
        employee: body.employee,
        name: body.name,
        effective_from: body.effective_from,
        gross_monthly: { amount: body.gross_monthly, currency: currency ?? "INR" },
        lines: body.lines,
      }),
    onSuccess: () => {
      setShowStructureForm(false);
      void queryClient.invalidateQueries({ queryKey: ["salary-structures"] });
    },
  });

  const [showComponentForm, setShowComponentForm] = useState(false);
  const [showStructureForm, setShowStructureForm] = useState(false);
  const [showComponentOverview, setShowComponentOverview] = useState(false);

  return (
    <>
      <PageHeader
        title="Salary"
        subtitle="Earning components and salary structures that the payroll compute reads."
        actions={
          <>
            <Button onClick={() => setShowComponentOverview((open) => !open)}>
              <Plus size={15} /> Components
            </Button>
            <Button variant="primary" onClick={() => setShowStructureForm((open) => !open)}>
              <Plus size={15} /> Add structure
            </Button>
          </>
        }
      />

      <div className="mb-6">
        <SalaryCard
          structures={structures.data?.results ?? []}
          pending={structures.isPending}
          timezone={timezone}
          currency={currency}
        />
      </div>

      {accountId ? (
        <div className="mb-6">
          <StatutorySettingsCard
            account={account.data}
            canEdit={canEdit}
            pending={account.isPending}
            saving={saveStatutory.isPending}
            saved={saveStatutory.isSuccess}
            error={saveStatutory.error instanceof ApiError ? saveStatutory.error : null}
            onSubmit={(values) => saveStatutory.mutate(values)}
          />
        </div>
      ) : null}

      {showStructureForm ? (
        <AddStructure
          employees={employees.data ?? []}
          components={components.data?.results ?? []}
          pending={createStructure.isPending}
          error={createStructure.error instanceof ApiError ? createStructure.error : null}
          onSubmit={(values) => createStructure.mutate(values)}
          onClose={() => {
            setShowStructureForm(false);
            createStructure.reset();
          }}
          closeOnSubmit={false}
        />
      ) : null}
      {showComponentOverview ? (
        <ComponentOverviewDialog
          components={components.data?.results ?? []}
          pending={components.isPending}
          toggling={updateComponent.isPending}
          deleting={deleteComponent.isPending}
          onToggle={(component) =>
            updateComponent.mutate({ id: component.id, body: { is_active: !component.is_active } })
          }
          onDelete={(component) => deleteComponent.mutate(component.id)}
          onAdd={() => {
            setShowComponentForm(true);
          }}
          onClose={() => setShowComponentOverview(false)}
        />
      ) : null}
      {showComponentForm ? (
        <AddComponent
          pending={createComponent.isPending}
          error={createComponent.error instanceof ApiError ? createComponent.error : null}
          onSubmit={(values) => {
            createComponent.mutate(values);
            setShowComponentForm(false);
          }}
          onClose={() => setShowComponentForm(false)}
        />
      ) : null}
    </>
  );
}

export type StatutorySettings = {
  pf_employee_pct?: string | null;
  pf_employer_pct?: string | null;
  esic_employee_pct?: string | null;
  esic_employer_pct?: string | null;
  professional_tax_monthly?: string | null;
  professional_tax_slab?: string | null;
};

type StatutoryWrite = {
  pf_employee_pct: string;
  pf_employer_pct: string;
  esic_employee_pct: string;
  esic_employer_pct: string;
  professional_tax_monthly: string;
  professional_tax_slab: string;
};

const STATUTORY_DEFAULTS: StatutoryWrite = {
  pf_employee_pct: "12.00",
  pf_employer_pct: "13.00",
  esic_employee_pct: "0.750",
  esic_employer_pct: "3.250",
  professional_tax_monthly: "200.00",
  professional_tax_slab: "20000.00",
};

export function statutoryValues(account: StatutorySettings): StatutoryWrite {
  return {
    pf_employee_pct: account.pf_employee_pct ?? STATUTORY_DEFAULTS.pf_employee_pct,
    pf_employer_pct: account.pf_employer_pct ?? STATUTORY_DEFAULTS.pf_employer_pct,
    esic_employee_pct: account.esic_employee_pct ?? STATUTORY_DEFAULTS.esic_employee_pct,
    esic_employer_pct: account.esic_employer_pct ?? STATUTORY_DEFAULTS.esic_employer_pct,
    professional_tax_monthly:
      account.professional_tax_monthly ?? STATUTORY_DEFAULTS.professional_tax_monthly,
    professional_tax_slab:
      account.professional_tax_slab ?? STATUTORY_DEFAULTS.professional_tax_slab,
  };
}

export function StatutorySettingsCard({
  account,
  canEdit,
  pending,
  saving,
  saved,
  error,
  onSubmit,
}: {
  account: StatutorySettings | undefined;
  canEdit: boolean;
  pending: boolean;
  saving: boolean;
  saved: boolean;
  error: ApiError | null;
  onSubmit: (values: StatutoryWrite) => void;
}) {
  const values = statutoryValues(account ?? {});
  const fieldErrors = error?.fields ?? {};
  const formErrors = [
    ...(fieldErrors.non_field_errors ?? []),
    ...(fieldErrors.detail ?? []),
  ];

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Statutory deductions (PF / ESI)
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Percentages apply to gross pay for employees whose PF / ESI flag is
            enabled on their record. Set a percentage to 0 to switch that
            contribution off for the account.
          </p>
        </div>
        {!canEdit ? (
          <span className="text-xs text-slate-400">Owner / admin only</span>
        ) : null}
      </div>

      {pending ? (
        <TableSkeleton rows={3} />
      ) : (
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit || saving) return;
            const data = new FormData(event.currentTarget);
            onSubmit({
              pf_employee_pct: String(data.get("pf_employee_pct") ?? "").trim(),
              pf_employer_pct: String(data.get("pf_employer_pct") ?? "").trim(),
              esic_employee_pct: String(data.get("esic_employee_pct") ?? "").trim(),
              esic_employer_pct: String(data.get("esic_employer_pct") ?? "").trim(),
              professional_tax_monthly: String(
                data.get("professional_tax_monthly") ?? "",
              ).trim(),
              professional_tax_slab: String(
                data.get("professional_tax_slab") ?? "",
              ).trim(),
            });
          }}
        >
          <Field label="PF employee %">
            <input
              type="number"
              name="pf_employee_pct"
              min="0"
              max="100"
              step="0.01"
              defaultValue={values.pf_employee_pct}
              disabled={!canEdit || saving}
              className={inputClass}
            />
            <FieldError errors={fieldErrors.pf_employee_pct} />
          </Field>
          <Field label="PF employer %">
            <input
              type="number"
              name="pf_employer_pct"
              min="0"
              max="100"
              step="0.01"
              defaultValue={values.pf_employer_pct}
              disabled={!canEdit || saving}
              className={inputClass}
            />
            <FieldError errors={fieldErrors.pf_employer_pct} />
          </Field>
          <Field label="ESI employee %">
            <input
              type="number"
              name="esic_employee_pct"
              min="0"
              max="100"
              step="0.001"
              defaultValue={values.esic_employee_pct}
              disabled={!canEdit || saving}
              className={inputClass}
            />
            <FieldError errors={fieldErrors.esic_employee_pct} />
          </Field>
          <Field label="ESI employer %">
            <input
              type="number"
              name="esic_employer_pct"
              min="0"
              max="100"
              step="0.001"
              defaultValue={values.esic_employer_pct}
              disabled={!canEdit || saving}
              className={inputClass}
            />
            <FieldError errors={fieldErrors.esic_employer_pct} />
          </Field>
          <Field label="Professional tax (monthly)">
            <input
              type="number"
              name="professional_tax_monthly"
              min="0"
              step="0.01"
              defaultValue={values.professional_tax_monthly}
              disabled={!canEdit || saving}
              className={inputClass}
            />
            <FieldError errors={fieldErrors.professional_tax_monthly} />
          </Field>
          <Field label="Professional tax slab">
            <input
              type="number"
              name="professional_tax_slab"
              min="0"
              step="0.01"
              defaultValue={values.professional_tax_slab}
              disabled={!canEdit || saving}
              className={inputClass}
            />
            <FieldError errors={fieldErrors.professional_tax_slab} />
          </Field>

          {canEdit ? (
            <div className="flex items-center justify-end gap-3 sm:col-span-2">
              {saved ? <span className="text-sm text-emerald-600">Saved.</span> : null}
              {error ? (
                <span role="alert" className="text-sm text-rose-600">
                  {formErrors.length ? formErrors.join(" ") : error.message}
                </span>
              ) : null}
              <Button type="submit" variant="primary" disabled={saving}>
                Save statutory settings
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Card>
  );
}

function FieldError({ errors }: { errors?: string[] | undefined }) {
  if (!errors?.length) return null;
  return (
    <span role="alert" className="mt-1 block text-sm text-rose-600">
      {errors.join(" ")}
    </span>
  );
}

export function ComponentOverviewDialog({
  components,
  pending,
  toggling,
  deleting,
  onToggle,
  onDelete,
  onAdd,
  onClose,
}: {
  components: SalaryComponent[];
  pending: boolean;
  toggling: boolean;
  deleting: boolean;
  onToggle: (component: SalaryComponent) => void;
  onDelete: (component: SalaryComponent) => void;
  onAdd: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title="Components" onClose={onClose} wide>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Earning and deduction components that structures reference.
        </p>
        <Button variant="primary" onClick={onAdd}>
          <Plus size={15} /> Add component
        </Button>
      </div>
      {pending ? (
        <TableSkeleton rows={3} />
      ) : components.length === 0 ? (
        <EmptyState title="No components yet" body="Add earning and deduction components first; structures reference them." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {components.map((component) => (
            <li key={component.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{component.name}</p>
                <p className="text-xs text-slate-500">{component.code}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={component.kind === "earning" ? "emerald" : "amber"}>
                  {component.kind ?? "—"}
                </Badge>
                {component.is_taxable ? <Badge>Taxable</Badge> : null}
                {component.is_active === false ? <Badge tone="rose">Inactive</Badge> : null}
                <Button
                  disabled={toggling}
                  onClick={() => onToggle(component)}
                  aria-label={component.is_active === false ? `Activate ${component.name}` : `Deactivate ${component.name}`}
                >
                  {component.is_active === false ? "Activate" : "Deactivate"}
                </Button>
                <Button
                  disabled={deleting}
                  onClick={() => onDelete(component)}
                  aria-label={`Delete ${component.name}`}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function SalaryCard({
  structures,
  pending,
  timezone,
  currency,
}: {
  structures: SalaryStructure[];
  pending: boolean;
  timezone?: string | null;
  currency?: string | null;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold text-slate-900">Structures</h2>
      {pending ? (
        <TableSkeleton rows={3} />
      ) : structures.length === 0 ? (
        <EmptyState title="No structures yet" body="A structure ties an employee to a gross pay and the lines that make it up." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {structures.map((structure) => {
            const expanded = open === structure.id;
            const lineCount = (structure.lines ?? []).length;
            const totals = structureTotals(structure);
            return (
              <li key={structure.id} className="py-2.5">
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : structure.id)}
                  aria-expanded={expanded}
                  className="flex w-full items-center justify-between gap-3 text-left"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {expanded ? (
                      <ChevronDown size={15} className="shrink-0 text-slate-400" />
                    ) : (
                      <ChevronRight size={15} className="shrink-0 text-slate-400" />
                    )}
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-slate-900">{structure.employee_name}</span>
                      <span className="block text-xs text-slate-500">
                        {structure.name || "—"} · {formatDate(structure.effective_from, timezone ?? undefined)}
                        {structure.effective_to ? ` – ${formatDate(structure.effective_to, timezone ?? undefined)}` : ""}
                      </span>
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="block text-sm font-semibold tabular-nums text-slate-900">
                      {formatMoney(money(totals.gross, currency))}
                    </span>
                    {structure.is_current ? <Badge tone="emerald">Current</Badge> : <Badge>Historical</Badge>}
                  </span>
                </button>
                {lineCount > 0 ? (
                  <p className="mt-1 pl-6 text-xs text-slate-500">
                    {`${lineCount} line${lineCount === 1 ? "" : "s"}`}
                  </p>
                ) : null}
                {expanded ? <StructureBreakdown structure={structure} currency={currency} /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Component-wise earnings and deductions, then the estimate they add up to. */
function StructureBreakdown({
  structure,
  currency,
}: {
  structure: SalaryStructure;
  currency?: string | null;
}) {
  const { gross, anchor, earnings, deductions, totalDeductions, estimatedNet } = structureTotals(structure);

  return (
    <div className="mt-2 ml-6 rounded-md border border-slate-100 bg-slate-50/70 p-3">
      <LineGroup title="Earnings" lines={earnings} gross={anchor} currency={currency} />
      <LineGroup title="Deductions" lines={deductions} gross={anchor} currency={currency} />
      <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-200 pt-2 text-xs">
        <div>
          <dt className="text-slate-500">Gross monthly</dt>
          <dd className="font-medium tabular-nums text-slate-900">
            {formatMoney(money(gross, currency))}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Deductions</dt>
          <dd className="font-medium tabular-nums text-slate-900">
            {formatMoney(money(totalDeductions, currency))}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Estimated net</dt>
          <dd className="font-semibold tabular-nums text-slate-900">
            {formatMoney(money(estimatedNet, currency))}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function LineGroup({
  title,
  lines,
  gross,
  currency,
}: {
  title: string;
  lines: SalaryStructureLine[];
  gross: number;
  currency?: string | null;
}) {
  if (lines.length === 0) return null;
  return (
    <div className="mt-1 first:mt-0">
      <p className="text-xs font-medium text-slate-600">{title}</p>
      <ul className="mt-1 space-y-0.5">
        {lines.map((line) => (
          <li key={line.id} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-slate-600">
              {line.component_name}
              <span className="ml-1 text-slate-400">
                {line.calculation_mode === "percent_of_gross"
                  ? `${Number(line.percent_of_gross ?? 0)}% of gross`
                  : "fixed"}
              </span>
            </span>
            <span className="tabular-nums text-slate-700">
              {formatMoney(money(lineAmount(line, gross), currency))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AddComponent({
  pending,
  error,
  onSubmit,
  onClose,
}: {
  pending: boolean;
  error: ApiError | null;
  onSubmit: (values: {
    name: string;
    code: string;
    kind?: ComponentKind;
    is_taxable?: boolean;
    is_active?: boolean;
  }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<ComponentKind>("earning");
  const [isTaxable, setIsTaxable] = useState(true);
  const [isActive, setIsActive] = useState(true);

  return (
    <Modal title="Add component" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim() || !code.trim()) return;
          onSubmit({
            name: name.trim(),
            code: code.trim(),
            kind,
            is_taxable: isTaxable,
            is_active: isActive,
          });
          setName("");
          setCode("");
          onClose();
        }}
        className="grid gap-3 sm:grid-cols-4"
      >
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required className={inputClass} />
        </Field>
        <Field label="Code">
          <input value={code} onChange={(e) => setCode(e.target.value)} required className={inputClass} />
        </Field>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value as ComponentKind)} className={inputClass}>
            <option value="earning">Earning</option>
            <option value="deduction">Deduction</option>
          </select>
        </Field>
        <div className="flex items-end gap-4 pb-2 text-sm text-slate-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isTaxable}
              onChange={(e) => setIsTaxable(e.target.checked)}
              className="size-4 accent-slate-900"
            />
            Taxable
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="size-4 accent-slate-900"
            />
            Active
          </label>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-rose-600 sm:col-span-4">
            {error.message}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 sm:col-span-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

export function validateRevisionEffectiveFrom(
  effectiveFrom: string,
  currentEffectiveFrom?: string,
): string | null {
  if (!effectiveFrom) return "Choose an effective date for the revision.";
  if (currentEffectiveFrom && effectiveFrom <= currentEffectiveFrom) {
    return "Choose a date after the current structure's effective date.";
  }
  return null;
}

/**
 * The create and revise form. A revision is a new effective-dated structure
 * with the current one prefilled, not an edit of history: the backend closes
 * the old structure and keeps it, so payroll snapshots never move.
 */
export function AddStructure({
  employees,
  components,
  pending,
  error,
  onSubmit,
  onClose,
  initial,
  employeeName,
  currency,
  title = "Add structure",
  submitLabel = "Save structure",
  closeOnSubmit = true,
}: {
  employees: Array<{ id: string; display_name: string }>;
  components: SalaryComponent[];
  pending: boolean;
  error: ApiError | null;
  onSubmit: (values: {
    employee: string;
    name?: string;
    effective_from: string;
    gross_monthly: string;
    lines: Array<{ component: string; calculation_mode?: LineMode; amount?: string | null; percent_of_gross?: string | null }>;
  }) => void;
  onClose: () => void;
  initial?: SalaryStructure;
  employeeName?: string;
  currency?: string | null;
  title?: string;
  submitLabel?: string;
  closeOnSubmit?: boolean;
}) {
  const [employee, setEmployee] = useState(initial?.employee ?? "");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [gross, setGross] = useState(initial?.gross_monthly?.amount ?? "");
  const [dateError, setDateError] = useState<string | null>(null);
  const [lines, setLines] = useState<Array<{
    component: string;
    calculation_mode: LineMode;
    amount: string;
    percent_of_gross: string;
  }>>(
    (initial?.lines ?? []).map((line) => ({
      component: line.component,
      calculation_mode:
        line.calculation_mode === "percent_of_gross" ? "percent_of_gross" : "amount",
      amount: line.amount ?? "",
      percent_of_gross: line.percent_of_gross ?? "",
    })),
  );

  function toggleLine(componentId: string) {
    setLines((current) =>
      current.some((line) => line.component === componentId)
        ? current.filter((line) => line.component !== componentId)
        : [...current, { component: componentId, calculation_mode: "amount", amount: "", percent_of_gross: "" }],
    );
  }

  // A component that was retired after the current structure was written must
  // still appear in a revision, otherwise saving would silently drop it.
  const knownComponentIds = new Set(components.map((component) => component.id));
  const historicalComponents: SalaryComponent[] = (initial?.lines ?? [])
    .filter((line) => !knownComponentIds.has(line.component))
    .map(
      (line) =>
        ({
          id: line.component,
          name: line.component_name || line.component,
          code: "",
          kind: line.component_kind || "earning",
          is_active: true,
          is_taxable: false,
        }) as SalaryComponent,
    );
  const availableComponents = [...components, ...historicalComponents];
  const componentLookup = new Map(availableComponents.map((component) => [component.id, component]));

  const previewLines = lines.map(
    (line, index) =>
      ({
        id: `draft-${index}`,
        component: line.component,
        component_name: componentLookup.get(line.component)?.name ?? line.component,
        component_kind: componentLookup.get(line.component)?.kind ?? "earning",
        calculation_mode: line.calculation_mode,
        amount: line.calculation_mode === "amount" ? line.amount || null : null,
        percent_of_gross:
          line.calculation_mode === "percent_of_gross" ? line.percent_of_gross || null : null,
      }) as SalaryStructureLine,
  );
  const previewStructure = {
    id: initial?.id ?? "draft",
    employee: employee || initial?.employee || "",
    employee_name: employeeName || initial?.employee_name || "",
    name: initial?.name ?? "Revision",
    effective_from: effectiveFrom || initial?.effective_from || "",
    gross_monthly: money(Number(gross) || 0, currency),
    is_current: true,
    lines: previewLines,
  } as SalaryStructure;
  const deductionError = validateDeductions(previewStructure);

  return (
    <Modal title={title} onClose={onClose} wide>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const problem = validateRevisionEffectiveFrom(effectiveFrom, initial?.effective_from);
          if (!employee) {
            setDateError("Choose an employee for this structure.");
            return;
          }
          if (problem) {
            setDateError(problem);
            return;
          }
          if (deductionError) {
            setDateError(null);
            return;
          }
          setDateError(null);
          onSubmit({
            employee,
            name: initial?.name,
            effective_from: effectiveFrom,
            gross_monthly: gross || "0",
            lines: lines
              .filter((line) => line.component)
              .map((line) => ({
                component: line.component,
                calculation_mode: line.calculation_mode,
                ...(line.calculation_mode === "amount"
                  ? { amount: line.amount || null }
                  : { percent_of_gross: line.percent_of_gross || null }),
              })),
          });
          if (closeOnSubmit) {
            onClose();
            setLines([]);
          }
        }}
        className="grid gap-4"
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Employee">
            {initial ? (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {employeeName || initial.employee_name}
              </p>
            ) : (
              <select value={employee} onChange={(e) => setEmployee(e.target.value)} required className={inputClass}>
                <option value="">Choose???</option>
                {employees.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Effective from">
            <input
              type="date"
              value={effectiveFrom}
              required
              onChange={(e) => {
                setEffectiveFrom(e.target.value);
                setDateError(null);
              }}
              className={inputClass}
            />
          </Field>
          <Field label="Gross monthly (INR)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={gross}
              placeholder="e.g. 45000"
              onChange={(e) => setGross(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        {dateError ? (
          <p role="alert" className="text-sm text-rose-600">
            {dateError}
          </p>
        ) : null}
        {deductionError ? (
          <p role="alert" className="text-sm text-rose-600">
            {deductionError}
          </p>
        ) : null}

        <div>
          <p className="mb-2 text-xs font-medium text-slate-600">Lines</p>
          {availableComponents.length === 0 ? (
            <p className="text-xs text-slate-500">
              Add components first so a structure has something to be made of.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {availableComponents
                .filter(
                  (component) =>
                    component.is_active !== false ||
                    lines.some((line) => line.component === component.id),
                )
                .map((component) => {
                const line = lines.find((l) => l.component === component.id);
                return (
                  <li key={component.id} className="flex flex-wrap items-center gap-2">
                    <label className="flex min-w-40 flex-1 items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(line)}
                        onChange={() => toggleLine(component.id)}
                        className="size-4 accent-slate-900"
                      />
                      {component.name}
                    </label>
                    {line ? (
                      <>
                        <select
                          value={line.calculation_mode}
                          onChange={(e) =>
                            setLines((current) =>
                              current.map((l) =>
                                l.component === component.id ? { ...l, calculation_mode: e.target.value as LineMode } : l,
                              ),
                            )
                          }
                          className={inputClass}
                        >
                          <option value="amount">Fixed amount</option>
                          <option value="percent_of_gross">% of gross</option>
                        </select>
                        {line.calculation_mode === "amount" ? (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Amount"
                            value={line.amount}
                            onChange={(e) =>
                              setLines((current) =>
                                current.map((l) => (l.component === component.id ? { ...l, amount: e.target.value } : l)),
                              )
                            }
                            className={inputClass}
                          />
                        ) : (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            placeholder="%"
                            value={line.percent_of_gross}
                            onChange={(e) =>
                              setLines((current) =>
                                current.map((l) =>
                                  l.component === component.id ? { ...l, percent_of_gross: e.target.value } : l,
                                ),
                              )
                            }
                            className={inputClass}
                          />
                        )}
                      </>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-slate-600">Before you save</p>
          <StructureBreakdown structure={previewStructure} currency={currency} />
        </div>

        {error ? (
          <div role="alert" className="space-y-1 text-sm text-rose-600">
            {Object.entries(error.fields).map(([field, messages]) => (
              <p key={field}>
                <span className="font-medium capitalize">{field.replace(/_/g, " ")}:</span>{" "}
                {messages.join(" ")}
              </p>
            ))}
            {Object.keys(error.fields).length === 0 ? <p>{error.message}</p> : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>
            <Trash2 size={14} /> Close
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}